import { newClientKey, type ChatMessage, type MessagePage } from '@deskworks/domain';
import { ApiRequestError } from './api.js';

export interface OutboxItem {
  clientKey: string;
  body: string;
  threadRootId: string | null;
  fileIds: string[];
  state: 'sending' | 'failed';
  error: string | null;
  attempts: number;
  createdAt: string;
}

export interface TimelineState {
  status: 'loading' | 'ready' | 'error' | 'gone';
  error: string | null;
  /** Server-confirmed messages for the main timeline (thread replies excluded), ordered by seq. */
  messages: ChatMessage[];
  /** Thread replies keyed by root id (only roots that have been opened or changed). */
  threads: Record<string, ChatMessage[]>;
  outbox: OutboxItem[];
  changeSeq: number;
  hasMoreBefore: boolean;
  loadingOlder: boolean;
}

export interface TimelineTransport {
  list(opts: { before?: number; thread?: string }): Promise<MessagePage>;
  changes(since: number): Promise<MessagePage>;
  post(input: { clientKey: string; body: string; threadRootId: string | null; fileIds: string[] }): Promise<ChatMessage>;
}

const MAX_AUTO_RETRIES = 5;

/**
 * Client-side conversation timeline. The server is the source of truth: messages are shown as
 * sent only after the server persisted them. Unsent messages stay in a visible outbox with their
 * retry key, so retries can never create duplicates. Missed realtime notices are repaired by
 * backfilling every change after the last known change sequence.
 */
export class ConversationStore {
  private state: TimelineState = {
    status: 'loading',
    error: null,
    messages: [],
    threads: {},
    outbox: [],
    changeSeq: 0,
    hasMoreBefore: false,
    loadingOlder: false,
  };
  private listeners = new Set<() => void>();
  private byId = new Map<string, ChatMessage>();
  private backfilling: Promise<void> | null = null;
  private backfillAgain = false;
  private retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private disposed = false;

  constructor(private readonly transport: TimelineTransport) {}

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };

  getSnapshot = () => this.state;

  private set(patch: Partial<TimelineState>) {
    if (this.disposed) return;
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  dispose() {
    this.disposed = true;
    for (const t of this.retryTimers.values()) clearTimeout(t);
  }

  /** Re-activates a store after dispose (React may mount, unmount and remount the same instance). */
  resume() {
    this.disposed = false;
  }

  async load() {
    try {
      const page = await this.transport.list({});
      this.byId.clear();
      this.merge(page.messages);
      this.set({ status: 'ready', error: null, changeSeq: page.changeSeq, hasMoreBefore: page.hasMoreBefore });
    } catch (err) {
      const e = err as ApiRequestError;
      this.set({ status: e.status === 404 ? 'gone' : 'error', error: e.message });
    }
  }

  async loadOlder() {
    const oldest = this.state.messages[0];
    if (!oldest || !this.state.hasMoreBefore || this.state.loadingOlder) return;
    this.set({ loadingOlder: true });
    try {
      const page = await this.transport.list({ before: oldest.seq });
      this.merge(page.messages);
      this.set({ hasMoreBefore: page.hasMoreBefore, loadingOlder: false });
    } catch {
      this.set({ loadingOlder: false });
    }
  }

  async openThread(rootId: string) {
    const page = await this.transport.list({ thread: rootId });
    this.merge(page.messages);
  }

  /** Fetch every change after the last known change sequence (single-flight). */
  backfill(knownChangeSeq?: number): Promise<void> {
    if (knownChangeSeq !== undefined && knownChangeSeq <= this.state.changeSeq) return Promise.resolve();
    if (this.backfilling) {
      this.backfillAgain = true;
      return this.backfilling;
    }
    this.backfilling = (async () => {
      try {
        do {
          this.backfillAgain = false;
          let more = true;
          while (more) {
            const page = await this.transport.changes(this.state.changeSeq);
            this.merge(page.messages);
            this.set({ changeSeq: Math.max(this.state.changeSeq, page.changeSeq) });
            more = page.hasMoreBefore && page.messages.length > 0;
          }
        } while (this.backfillAgain);
      } catch (err) {
        const e = err as ApiRequestError;
        if (e.status === 404) this.set({ status: 'gone', error: e.message });
      } finally {
        this.backfilling = null;
      }
    })();
    return this.backfilling;
  }

  private merge(list: ChatMessage[]) {
    if (list.length === 0) return;
    for (const m of list) {
      const prev = this.byId.get(m.id);
      if (!prev || m.changeSeq >= prev.changeSeq) this.byId.set(m.id, m);
    }
    const all = [...this.byId.values()];
    const messages = all.filter((m) => !m.threadRootId).sort((a, b) => a.seq - b.seq);
    const threads: Record<string, ChatMessage[]> = {};
    for (const m of all) {
      if (!m.threadRootId) continue;
      (threads[m.threadRootId] ??= []).push(m);
    }
    for (const k of Object.keys(threads)) threads[k]!.sort((a, b) => a.seq - b.seq);
    // Confirmed messages leave the outbox.
    const confirmed = new Set(list.map((m) => m.clientKey).filter(Boolean));
    const outbox = this.state.outbox.filter((o) => !confirmed.has(o.clientKey));
    this.set({ messages, threads, outbox });
  }

  send(body: string, opts: { threadRootId?: string | null; fileIds?: string[] } = {}) {
    const item: OutboxItem = {
      clientKey: newClientKey(),
      body,
      threadRootId: opts.threadRootId ?? null,
      fileIds: opts.fileIds ?? [],
      state: 'sending',
      error: null,
      attempts: 0,
      createdAt: new Date().toISOString(),
    };
    this.set({ outbox: [...this.state.outbox, item] });
    void this.attempt(item.clientKey);
    return item.clientKey;
  }

  retry(clientKey: string) {
    const t = this.retryTimers.get(clientKey);
    if (t) clearTimeout(t);
    this.updateOutbox(clientKey, { state: 'sending', error: null });
    void this.attempt(clientKey);
  }

  discard(clientKey: string) {
    this.set({ outbox: this.state.outbox.filter((o) => o.clientKey !== clientKey) });
  }

  /**
   * Restores unsent items kept on a trusted device. They keep their original retry keys, so if the
   * server already stored one, retrying returns the original message instead of a duplicate.
   */
  restoreOutbox(items: OutboxItem[]) {
    const known = new Set(this.state.outbox.map((o) => o.clientKey));
    const confirmed = new Set(this.state.messages.map((m) => m.clientKey).filter(Boolean));
    const fresh = items.filter((i) => !known.has(i.clientKey) && !confirmed.has(i.clientKey));
    if (!fresh.length) return;
    this.set({ outbox: [...this.state.outbox, ...fresh.map((i) => ({ ...i, state: 'failed' as const, error: 'Not sent yet.' }))] });
    for (const i of fresh) this.retry(i.clientKey);
  }

  /** Retry everything that failed for transient reasons (called on reconnect). */
  retryFailed() {
    for (const o of this.state.outbox) if (o.state === 'failed') this.retry(o.clientKey);
  }

  private updateOutbox(clientKey: string, patch: Partial<OutboxItem>) {
    this.set({ outbox: this.state.outbox.map((o) => (o.clientKey === clientKey ? { ...o, ...patch } : o)) });
  }

  private async attempt(clientKey: string) {
    const item = this.state.outbox.find((o) => o.clientKey === clientKey);
    if (!item) return;
    this.updateOutbox(clientKey, { attempts: item.attempts + 1 });
    try {
      const msg = await this.transport.post({ clientKey, body: item.body, threadRootId: item.threadRootId, fileIds: item.fileIds });
      this.merge([msg]);
      if (msg.changeSeq > this.state.changeSeq + 1) void this.backfill();
      else this.set({ changeSeq: Math.max(this.state.changeSeq, msg.changeSeq) });
    } catch (err) {
      const e = err as ApiRequestError;
      const transient = e instanceof ApiRequestError ? e.transient : true;
      const attempts = (this.state.outbox.find((o) => o.clientKey === clientKey)?.attempts ?? 1);
      this.updateOutbox(clientKey, {
        state: 'failed',
        error: transient ? 'Not sent yet. DeskWorks One will keep trying.' : e.message,
      });
      if (transient && attempts < MAX_AUTO_RETRIES) {
        const delay = Math.min(30_000, 1000 * 2 ** attempts);
        this.retryTimers.set(clientKey, setTimeout(() => this.retry(clientKey), delay));
      }
    }
  }
}
